'use client';

import './nav-stack.scss';

import { useRouter } from 'next/navigation';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@sola/ark-core/atoms/badge/badge.js';
import { LinkStyle } from '@sola/ark-core/atoms/link-style/link-style.js';
import { Link } from '@sola/ark-core/atoms/link/link.js';
import { cn } from '@sola/ark-core/utils/helpers.js';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.js';
import { AuthStatusEnum, useAuth } from '@sola/auth-sdk/store.js';
import { getPublicConfig } from '@sola/config/lib/getters.js';
import { trackClick } from '@sola/cookies/components/datalayer.js';
import { NavigationProps } from '@sola/schema/lib/schema.js';

import { Dialpad } from '../../atoms/dialpad/dialpad';
import { LinkList } from '../../atoms/link-list/link-list';

type NavStackProps = {
  items: NavigationProps[][];
  setItems: React.Dispatch<React.SetStateAction<NavigationProps[][]>>;
  showMyAccountSection?: boolean;
  className?: string;
};

// TODO: this should be its own component
const MobileAccountSection: FC = () => {
  const { NEXT_PUBLIC_BASE_URL_MYACCOUNT } = getPublicConfig();
  const { actions: authActions } = useAuth('actions');
  const { currentStatus: currentAuthStatus } = useAuth('currentStatus');
  const { t } = useTranslation(['priority']);

  const logOut = async () => {
    trackClick(t('nav.logOut'));
    authActions.logOut();
  };

  return (
    <div className="NavStack__mobile-accounts-section p-comfy-sm sm:p-comfy-xl">
      <div className="flex flex-col">
        <span className="flex py-snug-xl gap-comfy-xs items-center">
          <ArkIcon
            iconName="user-01"
            className="NavStack__icon NavStack__icon--filled"
          />
          {NEXT_PUBLIC_BASE_URL_MYACCOUNT && (
            <LinkStyle size="xl" noUnderline noPadding purpose="neutral">
              <Link
                href={NEXT_PUBLIC_BASE_URL_MYACCOUNT}
                className="label label-lg label-potent"
              >
                {t('header.myAccount')}
              </Link>
            </LinkStyle>
          )}
        </span>

        {currentAuthStatus === AuthStatusEnum.Authenticated && (
          <span className="flex py-snug-xl gap-comfy-xs items-center">
            <ArkIcon iconName="log-out-01" className="NavStack__icon" />
            <LinkStyle size="xl" noUnderline noPadding purpose="negative">
              <button type="button" onClick={logOut}>
                {t('nav.logOut')}
              </button>
            </LinkStyle>
          </span>
        )}

        <span className="flex py-snug-xl gap-comfy-xs items-center">
          <ArkIcon iconName="message-chat-circle" className="NavStack__icon" />
          <LinkStyle size="xl" noUnderline noPadding purpose="neutral">
            <Link
              href="/about-us/contact"
              className="label label-lg label-potent"
            >
              {t('header.contactUs')}
            </Link>
          </LinkStyle>
        </span>
      </div>
    </div>
  );
};

export const NavStack: React.FC<NavStackProps> = ({
  items,
  setItems,
  showMyAccountSection,
  className,
}) => {
  const router = useRouter();
  const [currentHeading, setCurrentHeading] = useState<string[]>([]);
  const [currentHeadingLink, setCurrentHeadingLink] = useState<string[]>([]);

  const activeNavItems = items[items.length - 1];
  const stackIndex = items.length - 1;
  const isRootNav = items.length === 1;
  const useSmallLinkList =
    activeNavItems.every((i) => !i.items?.length) &&
    activeNavItems.every((i) => !i.description);
  const hasIcons = activeNavItems.some((i) => Boolean(i.icon));

  return (
    <div
      key={activeNavItems.map((i) => i.title).join()}
      className={cn(
        'NavStack py-comfy-sm flex flex-col gap-comfy-sm',
        className,
      )}
    >
      <div className="flex flex-col gap-comfy-sm">
        {!isRootNav && (
          <div className="p-comfy-sm sm:px-comfy-xl flex flex-col gap-snug-xl">
            <div className="NavStack__top-link-container pb-snug-md">
              <LinkStyle
                size="xl"
                noPadding
                noUnderline
                iconAfter="arrow-right"
              >
                <Link href={currentHeadingLink[stackIndex - 1] || '/'}>
                  {currentHeading[stackIndex - 1]}
                </Link>
              </LinkStyle>
            </div>
          </div>
        )}

        {useSmallLinkList ? (
          <div className="px-comfy-sm sm:px-comfy-xl">
            <LinkList item={{ items: activeNavItems }} />
          </div>
        ) : (
          <ul className="gap-snug-xl flex flex-col m-none p-none">
            {activeNavItems.map((item) => {
              const hasChildItems = item?.items?.length;
              const { displayVariant } = item;
              return (
                <li
                  className={cn('flex flex-col m-none p-none', {
                    'NavStack__items--border mt-comfy-xs': displayVariant,
                  })}
                  key={item.title}
                >
                  <div
                    className={cn('flex justify-between', {
                      'flex-col items-start': displayVariant,
                      'items-center': !displayVariant,
                    })}
                  >
                    <button
                      type="button"
                      onClick={() => {
                        if (!hasChildItems && item.link) {
                          trackClick(
                            item.title || item.description || 'Unknown',
                            item.link,
                            'NavStack__link',
                          );
                          if (item?.target === '_blank') {
                            window.open(
                              item?.link,
                              item.target,
                              'noopener,noreferrer',
                            );
                          } else {
                            router.push(item?.link);
                          }
                          return;
                        }

                        if (item.items) {
                          // keep track of the section you drilled into
                          // rather crude way of creating a navigation stack and updating it as things are pushed and popped
                          setCurrentHeading((h) => {
                            const updatedHeading = [...(h || [])];
                            updatedHeading[stackIndex] = item.title || '';
                            return updatedHeading;
                          });
                          setCurrentHeadingLink((h) => {
                            const updatedHeadingLinks = [...(h || [])];
                            updatedHeadingLinks[stackIndex] = item?.link || '';
                            return updatedHeadingLinks;
                          });
                          setItems((ni) => [
                            ...ni,
                            item.items as NavigationProps[],
                          ]);
                        }
                      }}
                      className={cn(
                        'NavStack__link flex justify-between items-center w-full px-comfy-sm sm:px-comfy-xl py-snug-xl',
                        {
                          'gap-snug-md': item.description,
                          'py-comfy-sm': displayVariant,
                        },
                      )}
                    >
                      <div className="flex gap-comfy-xs">
                        {item?.icon && (
                          <ArkIcon
                            iconName={item.icon}
                            className="NavStack__icon"
                          />
                        )}
                        <div
                          className={cn('flex flex-col', {
                            'NavStack__item-title--padding':
                              hasIcons && !item?.icon,
                            'NavStack__item-title--padding--display-variant':
                              displayVariant,
                            'gap-snug-md': item.description,
                            'pr-roomy-sm': !hasChildItems,
                          })}
                        >
                          <span className="flex gap-snug-xl items-center">
                            <p
                              className={cn(
                                'NavStack__text label label-lg label-neutral label-potent flex gap-snug-xl items-center',
                                {
                                  'label-xl label-potent': isRootNav,
                                },
                              )}
                            >
                              {item.title}
                            </p>
                            {item.badge?.text && (
                              <span className="NavStack__badge">
                                <Badge
                                  label={item.badge?.text}
                                  color={item.badge?.color}
                                  size="sm"
                                />
                              </span>
                            )}
                          </span>
                          <p className="NavStack__description label-md label-moderate">
                            {item.description}
                          </p>
                        </div>
                      </div>

                      {Boolean(hasChildItems) && !displayVariant && (
                        <div
                          className={cn(
                            'NavStack__next-button ml-snug-md p-comfy-xs',
                          )}
                        >
                          <ArkIcon iconName="chevron-right" />
                        </div>
                      )}
                    </button>

                    {Boolean(hasChildItems) && displayVariant === 'grid' && (
                      <div className="pt-snug-xl px-comfy-sm sm:px-comfy-xl">
                        <Dialpad item={item} />
                      </div>
                    )}

                    {Boolean(hasChildItems) && displayVariant === 'social' && (
                      <div className="pt-snug-xl">
                        <LinkList item={item} />
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      {isRootNav && showMyAccountSection && <MobileAccountSection />}
    </div>
  );
};
